import { SIXTEENTHS } from "../movement/step.ts";
import { MAX_U16 } from "../protocol/limits.ts";
import { VISION_CAP_TILES } from "./vision.ts";

/**
 * Exploration (ADR-100 decisions 20 and 21): a map is divided into fog cells of 8 by 8 tiles, one bit each, and a cell
 * is explored once its centre has lain inside the default vision rectangle, 27 by 15 tiles around the character's feet
 * point, its edges included. The map server marks where a character is placed and the page marks its own character
 * as it moves, both through `visionCells` and `markVision`, so the two copies agree without a message per step. A set
 * is plain data (counts and bytes) and marking it allocates nothing.
 *
 * @example
 * const set = newExploredSet(fogCells(256), fogCells(256));
 * const last = newVisionRange();
 * const next = newVisionRange();
 * visionCells(feetX, feetY, set.columns, set.rows, next);
 * if (markVision(set, last, next)) {
 *   // the set changed: a keeper marks it dirty
 * }
 */

/**
 * The side of a fog cell, in tiles.
 */
export const FOG_CELL_TILES = 8;

const CELL_SIXTEENTHS = FOG_CELL_TILES * SIXTEENTHS;
const CENTRE_SIXTEENTHS = CELL_SIXTEENTHS / 2;
const HALF_WIDTH_SIXTEENTHS = (VISION_CAP_TILES.width * SIXTEENTHS) / 2;
const HALF_HEIGHT_SIXTEENTHS = (VISION_CAP_TILES.height * SIXTEENTHS) / 2;

/**
 * A character's exploration of one map: its counts of fog cells across and down, and one bit a cell, row-major, cell
 * `row * columns + column` the bit `index & 7` of byte `index >> 3`. Bits past the last cell are 0.
 */
export interface ExploredSet {
  readonly columns: number;
  readonly rows: number;
  readonly cells: Uint8Array;
}

/**
 * The fog cells along one side of a map, a short last cell counted whole.
 *
 * @param tiles The map's side in tiles.
 * @returns `ceil(tiles / 8)`: 32 for 256 tiles, 33 for 257.
 */
export function fogCells(tiles: number): number {
  return Math.ceil(tiles / FOG_CELL_TILES);
}

/**
 * Bytes of a set's cells.
 *
 * @param columns Fog cells across.
 * @param rows Fog cells down.
 * @returns `ceil(columns * rows / 8)`: 128 for 32 by 32.
 */
export function exploredBytes(columns: number, rows: number): number {
  return Math.ceil((columns * rows) / 8);
}

/**
 * A set with nothing explored.
 *
 * @param columns Fog cells across, 1 to 65535.
 * @param rows Fog cells down, 1 to 65535.
 * @returns The empty set.
 * @throws RangeError when a count is not a whole number from 1 to 65535, the widths `explored` carries.
 */
export function newExploredSet(columns: number, rows: number): ExploredSet {
  if (!Number.isInteger(columns) || !Number.isInteger(rows) || columns < 1 || rows < 1 || columns > MAX_U16 || rows > MAX_U16) {
    throw new RangeError(`an explored set is 1 to ${MAX_U16} fog cells each way, not ${columns} by ${rows}`);
  }
  return { columns, rows, cells: new Uint8Array(exploredBytes(columns, rows)) };
}

/**
 * A range of fog cells for `visionCells` to write, made once a session: the first column, the first row, the column
 * past the last and the row past the last. All zeros is the empty range, which is where a range starts.
 *
 * @returns The empty range.
 */
export function newVisionRange(): Int32Array {
  return new Int32Array(4);
}

/**
 * The fog cells whose centres lie inside the default vision rectangle around a feet point, clamped to the map. The
 * rectangle is 27 by 15 tiles, edges included, so it covers at most 4 columns and 2 rows. A rectangle that holds no
 * centre of the map writes the empty range, all zeros, so two empty ranges compare equal.
 *
 * @param x The feet point's x, in sixteenths of a tile.
 * @param y The feet point's y, in sixteenths of a tile.
 * @param columns The map's width in fog cells.
 * @param rows The map's height in fog cells.
 * @param out The range to write: first column, first row, end column, end row.
 */
export function visionCells(x: number, y: number, columns: number, rows: number, out: Int32Array): void {
  const firstColumn = Math.max(0, Math.ceil((x - HALF_WIDTH_SIXTEENTHS - CENTRE_SIXTEENTHS) / CELL_SIXTEENTHS));
  const endColumn = Math.min(columns, Math.floor((x + HALF_WIDTH_SIXTEENTHS - CENTRE_SIXTEENTHS) / CELL_SIXTEENTHS) + 1);
  const firstRow = Math.max(0, Math.ceil((y - HALF_HEIGHT_SIXTEENTHS - CENTRE_SIXTEENTHS) / CELL_SIXTEENTHS));
  const endRow = Math.min(rows, Math.floor((y + HALF_HEIGHT_SIXTEENTHS - CENTRE_SIXTEENTHS) / CELL_SIXTEENTHS) + 1);
  if (firstColumn >= endColumn || firstRow >= endRow) {
    out[0] = 0;
    out[1] = 0;
    out[2] = 0;
    out[3] = 0;
    return;
  }
  out[0] = firstColumn;
  out[1] = firstRow;
  out[2] = endColumn;
  out[3] = endRow;
}

/**
 * Marks a range explored unless it equals the last range marked. An equal range costs four comparisons and writes
 * nothing; another range sets its cells' bits (at most 8) and becomes the last.
 *
 * @param set The character's set.
 * @param last The range marked last, which this replaces.
 * @param next The range `visionCells` wrote for where the character stands now, inside the set's counts.
 * @returns True when a cell not explored before was set, the change a keeper marks dirty.
 */
export function markVision(set: ExploredSet, last: Int32Array, next: Int32Array): boolean {
  if (last[0] === next[0] && last[1] === next[1] && last[2] === next[2] && last[3] === next[3]) {
    return false;
  }
  last[0] = next[0] as number;
  last[1] = next[1] as number;
  last[2] = next[2] as number;
  last[3] = next[3] as number;
  let changed = false;
  for (let row = next[1] as number; row < (next[3] as number); row += 1) {
    for (let column = next[0] as number; column < (next[2] as number); column += 1) {
      const index = row * set.columns + column;
      const byte = set.cells[index >> 3] as number;
      const bit = 1 << (index & 7);
      if ((byte & bit) === 0) {
        set.cells[index >> 3] = byte | bit;
        changed = true;
      }
    }
  }
  return changed;
}

/**
 * Whether one fog cell is explored.
 *
 * @param set The set.
 * @param column The cell's column, 0 to `columns - 1`.
 * @param row The cell's row, 0 to `rows - 1`.
 * @returns True when its bit is set; false for a cell outside the set.
 */
export function isExplored(set: ExploredSet, column: number, row: number): boolean {
  if (column < 0 || row < 0 || column >= set.columns || row >= set.rows) {
    return false;
  }
  const index = row * set.columns + column;
  return ((set.cells[index >> 3] as number) & (1 << (index & 7))) !== 0;
}
